import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// --- Product Scanner ---
interface ScannedProduct {
  title: string;
  editionLabel: string;
  priceNumber: number;
  priceText: string;
  thumbnailUrl: string;
}
function scanPageForProducts(): ScannedProduct[] {
  const products: ScannedProduct[] = [];
  // Try JSON-LD first
  const jsonLdScripts = document.querySelectorAll('script[type="application/ld+json"]');
  jsonLdScripts.forEach(script => {
    try {
      const data = JSON.parse(script.textContent || '');
      if (data['@type'] === 'Product' && data.offers) {
        products.push({
          title: data.name || 'Producto',
          editionLabel: data.description || '',
          priceNumber: parseFloat(data.offers.price) || 0,
          priceText: `$${data.offers.price} ${data.offers.priceCurrency || 'USD'}`,
          thumbnailUrl: Array.isArray(data.image) ? data.image[0] : data.image || ''
        });
      }
    } catch {}
  });
  if (products.length > 0) return products;
  // Scan DOM for price patterns and nearby product info
  const priceRegex = /\$\s?(\d+(?:\.\d{1,2})?)/g;
  const bodyText = document.body.innerText;
  const priceMatches = [...bodyText.matchAll(priceRegex)];
  const prices = [...new Set(priceMatches.map(m => parseFloat(m[1])))];
  // Look for product images
  const images = document.querySelectorAll('img[src*="cdn"], img[src*="upload"], img[src*="product"]');
  const thumbnailUrl = images.length > 0 ? (images[0] as HTMLImageElement).src : '';
  // Look for H1 as product title
  const h1 = document.querySelector('h1');
  const title = h1?.textContent?.trim() || 'Protocolo Noche de Paz';
  if (prices.length > 0) {
    // Use the lowest price found (likely the product price)
    const mainPrice = Math.min(...prices.filter(p => p > 0));
    products.push({
      title,
      editionLabel: 'Guía Digital PDF',
      priceNumber: mainPrice,
      priceText: `$${mainPrice.toFixed(2)} USD`,
      thumbnailUrl
    });
  }
  return products;
}
// --- Fallback product ---
const FALLBACK_PRODUCTS: ScannedProduct[] = [{
  title: 'Protocolo Noche de Paz',
  editionLabel: 'Guía Digital PDF',
  priceNumber: 6.36,
  priceText: '$6.36 USD',
  thumbnailUrl: 'https://cdn.magicpatterns.com/uploads/3auo8EizvFWzv5oyNT7Jh7/BANER_PV_PROTOCOLO_NOCHE_DE_PAZ_EL_METODO_DE_3_DIAS.png'
}];
// --- Name & City pools ---
const NAMES = ['María', 'Ana', 'Laura', 'Carmen', 'Sofía', 'Valentina', 'Isabella', 'Camila', 'Lucía', 'Gabriela', 'Andrea', 'Daniela', 'Paula', 'Fernanda', 'Alejandra', 'Natalia', 'Carolina', 'Mariana', 'Diana', 'Paola', 'Claudia', 'Verónica', 'Adriana', 'Mónica', 'Patricia', 'Rosa', 'Elena', 'Lorena', 'Sandra', 'Catalina', 'Jimena', 'Renata', 'Valeria', 'Ximena', 'Karla', 'Melissa', 'Estefanía', 'Bianca', 'Alicia', 'Teresa', 'Rocío', 'Silvia', 'Beatriz', 'Julia', 'Inés'];
const CITIES = ['Ciudad de México', 'Bogotá', 'Lima', 'Buenos Aires', 'Santiago', 'Guadalajara', 'Medellín', 'Quito', 'Monterrey', 'Caracas', 'San José', 'Panamá', 'Santo Domingo', 'Guayaquil', 'Córdoba', 'Puebla', 'Cali', 'Arequipa', 'Rosario', 'Valparaíso', 'Barranquilla', 'Cancún', 'Cartagena', 'Cusco', 'Mendoza', 'La Paz', 'Montevideo', 'Tegucigalpa', 'San Salvador', 'Managua'];
// --- Utilities ---
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function randomInterval(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}
// --- Time ago generator ---
function randomTimeAgo(): string {
  const minutes = randomInterval(1, 45);
  return `hace ${minutes} min`;
}
// --- Component ---
interface ToastData {
  id: number;
  name: string;
  city: string;
  product: ScannedProduct;
  timeAgo: string;
}
export function RecentPurchaseToast() {
  const [toast, setToast] = useState<ToastData | null>(null);
  const [products, setProducts] = useState<ScannedProduct[]>(FALLBACK_PRODUCTS);
  const nameHistory = useRef<string[]>([]);
  const toastCounter = useRef(0);
  const prefersReducedMotion = useRef(false);
  // Check reduced motion preference
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    prefersReducedMotion.current = mq.matches;
    const handler = (e: MediaQueryListEvent) => {
      prefersReducedMotion.current = e.matches;
    };
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  // Scan products on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      const scanned = scanPageForProducts();
      if (scanned.length > 0) {
        setProducts(scanned);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, []);
  const pickName = useCallback((): string => {
    const available = NAMES.filter(n => !nameHistory.current.includes(n));
    const pool = available.length > 0 ? available : NAMES;
    const name = pickRandom(pool);
    nameHistory.current.push(name);
    if (nameHistory.current.length > 10) {
      nameHistory.current.shift();
    }
    return name;
  }, []);
  const showToast = useCallback(() => {
    const name = pickName();
    const city = pickRandom(CITIES);
    const product = pickRandom(products);
    const timeAgo = randomTimeAgo();
    toastCounter.current += 1;
    setToast({
      id: toastCounter.current,
      name,
      city,
      product,
      timeAgo
    });
    // Auto-dismiss after 7 seconds
    setTimeout(() => {
      setToast(null);
    }, 7000);
  }, [products, pickName]);
  // Timer loop
  useEffect(() => {
    // First toast after 3 seconds
    const firstTimer = setTimeout(() => {
      showToast();
    }, 3000);
    // Then repeat at random intervals
    let intervalTimer: ReturnType<typeof setTimeout>;
    const scheduleNext = () => {
      const delay = randomInterval(8000, 15000);
      intervalTimer = setTimeout(() => {
        showToast();
        scheduleNext();
      }, delay);
    };
    // Start the loop after the first toast + its display time
    const loopStarter = setTimeout(() => {
      scheduleNext();
    }, 3000 + 4500);
    return () => {
      clearTimeout(firstTimer);
      clearTimeout(loopStarter);
      clearTimeout(intervalTimer);
    };
  }, [showToast]);
  const reduced = prefersReducedMotion.current;
  return <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[9999] pointer-events-none" data-id="element-247">
      <AnimatePresence mode="wait" data-id="element-248">
        {toast && <motion.div key={toast.id} initial={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} animate={reduced ? {
        opacity: 1
      } : {
        opacity: 1,
        y: 0
      }} exit={reduced ? {
        opacity: 0
      } : {
        opacity: 0,
        y: 40
      }} transition={{
        duration: reduced ? 0.15 : 0.4,
        ease: 'easeOut'
      }} className="pointer-events-auto bg-white rounded-2xl shadow-xl shadow-midnight/15 border border-gray-100 px-4 py-3 flex items-center gap-3 max-w-sm md:max-w-md w-max" data-id="element-249">
            {/* Product thumbnail */}
            <div className="w-12 h-12 rounded-xl overflow-hidden shrink-0 bg-cream" data-id="element-250">
              {toast.product.thumbnailUrl ? <img src={toast.product.thumbnailUrl} alt={toast.product.title} className="w-full h-full object-cover" data-id="element-251" /> : <div className="w-full h-full flex items-center justify-center text-gold text-lg" data-id="element-252">
                  📦
                </div>}
            </div>

            {/* Text content */}
            <div className="min-w-0" data-id="element-253">
              <p className="text-sm text-[#333] leading-snug" data-id="element-254">
                <strong className="text-midnight" data-id="element-255">{toast.name}</strong> en{' '}
                {toast.city}
              </p>
              <p className="text-xs text-[#333]/70 leading-snug mt-0.5" data-id="element-256">
                compró{' '}
                <strong className="text-midnight" data-id="element-257">
                  {toast.product.editionLabel}
                </strong>
                {' — '}
                <span className="text-gold font-bold" data-id="element-258">
                  {toast.product.priceText}
                </span>
              </p>
              <p className="text-[10px] text-[#999] mt-0.5" data-id="element-259">{toast.timeAgo}</p>
            </div>

            {/* Verified badge */}
            <div className="shrink-0" data-id="element-260">
              <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center" data-id="element-261">
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-262">
                  <path d="M2.5 6L5 8.5L9.5 3.5" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-263" />
                </svg>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}